import React, { useState } from "react"
import {
  Box,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemAvatar,
  makeStyles,
} from "@material-ui/core"
import { AddLocationOutlined } from "@material-ui/icons"
import {
  RequirePermissions,
  NoItemsMessage,
  Icon,
  InlineEditTextField,
  GroupCreator,
  RowBox,
  LoadingSpinner,
  DeleteConfirmIconButton,
} from "../../components"
import {
  makeUpdateGroupOptimisticResponse,
  useMutationDeleteGroup,
  useMutationUpdateGroup,
  useQueryAdminableGroups,
} from "../../data"
import { toId } from "../../utils"
import SettingsFooter from "../../components/Settings/SettingsFooter"

const useStyles = makeStyles((theme) => ({
  avatar: {
    minWidth: "auto",
    marginRight: theme.spacing(1),
  },
  text: {
    padding: 4,
  },
}))

const GroupListItem = ({ group, onDelete }) => {
  const classes = useStyles()
  const [updateGroup] = useMutationUpdateGroup()
  const { name } = group

  const handleEdited = async (newValue) => {
    if (!newValue.trim() || newValue === name) {
      return
    }
    const optimisticResponse = makeUpdateGroupOptimisticResponse({ group, name })
    await updateGroup({
      variables: {
        id: toId(group),
        name: newValue,
      },
      optimisticResponse,
    })
  }

  const handleDelete = () => {
    onDelete && onDelete(group)
  }

  return (
    <ListItem>
      <ListItemAvatar classes={{ root: classes.avatar }}>
        <RowBox>
          <Icon name="group" />
        </RowBox>
      </ListItemAvatar>
      <ListItemText
        primaryTypographyProps={{ component: "div" }}
        primary={
          <InlineEditTextField
            displayComponent="div"
            value={name}
            onEdited={handleEdited}
            editable="group_update"
            className={classes.text}
          />
        }
      />
      <ListItemSecondaryAction>
        <DeleteConfirmIconButton onDelete={handleDelete} />
      </ListItemSecondaryAction>
    </ListItem>
  )
}

const Groups = () => {
  const { data, refetch, loading } = useQueryAdminableGroups()
  const [deleteGroup] = useMutationDeleteGroup()
  const [open, setOpen] = useState(false)

  const handleCreate = () => {
    setOpen(true)
  }
  const handleCreateClose = () => {
    setOpen(false)
    refetch()
  }

  const handleDelete = async (group) => {
    await deleteGroup({
      variables: {
        id: toId(group),
      },
    })
    refetch()
  }

  return (
    <>
      <GroupCreator open={open} onClose={handleCreateClose} />

      <Box mb={5} data-cy="Groups-list">
        {data && (
          <List disablePadding>
            {data.adminableGroups.map((group, index) => (
              <React.Fragment key={toId(group)}>
                {index > 0 && <Divider />}
                <GroupListItem key={toId(group)} group={group} onDelete={handleDelete} />
              </React.Fragment>
            ))}
          </List>
        )}

        {!data && loading && (
          <Box display="flex" justifyContent="center">
            <LoadingSpinner size={60} />
          </Box>
        )}

        {data?.adminableGroups.length === 0 && <NoItemsMessage>No areas of work found</NoItemsMessage>}
      </Box>

      <RequirePermissions requires="group_create">
        <SettingsFooter>
          <Button variant="contained" color="primary" onClick={handleCreate} data-cy="Button-create-group">
            <AddLocationOutlined /> <Box ml={1}>Add area of work</Box>
          </Button>
        </SettingsFooter>
      </RequirePermissions>
    </>
  )
}

export default Groups
